import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { downloadPngBlob } from '@/lib/share-card/domToPng'
import { copyTextWithFallback } from '@/lib/rouletteShareActions'
import {
  canShareParticipantFile,
  shareParticipantFile,
  type ParticipantShareModel,
} from './participant-share'
import styles from './ParticipantShareDialog.module.css'

export type PreparedParticipantShare = {
  model: ParticipantShareModel
  file: File
  previewUrl: string
}

type Props = {
  prepared: PreparedParticipantShare | null
  error: string | null
  onClose: () => void
  onRetry: () => void
  onShareAction?: () => void
}

export function ParticipantShareDialog({
  prepared,
  error,
  onClose,
  onRetry,
  onShareAction,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const sharing = useRef(false)
  const [isSharing, setIsSharing] = useState(false)
  const [notice, setNotice] = useState('')
  const [actionError, setActionError] = useState('')
  const [whatsappFallback, setWhatsappFallback] = useState(false)
  const supportsFiles = prepared ? canShareParticipantFile(prepared.file) : false

  useEffect(() => {
    const element = dialog.current
    element?.showModal()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      element?.close()
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const download = () => {
    if (!prepared) return
    downloadPngBlob(prepared.file, prepared.file.name)
    onShareAction?.()
    setActionError('')
    setNotice(
      'Imagem pronta para salvar. Publique nos Stories e adicione o link da campanha.'
    )
  }
  const share = async () => {
    if (!prepared || sharing.current) return
    sharing.current = true
    setIsSharing(true)
    setActionError('')
    setNotice('')
    try {
      const outcome = await shareParticipantFile(prepared.file, prepared.model)
      if (outcome === 'opened') {
        onShareAction?.()
        setNotice('Imagem e mensagem entregues ao compartilhamento do aparelho.')
      }
    } catch {
      setActionError(
        'Não foi possível compartilhar por aqui. Baixe a imagem e copie a mensagem abaixo.'
      )
    } finally {
      sharing.current = false
      setIsSharing(false)
    }
  }
  const copy = async (linkOnly = false) => {
    if (!prepared) return
    setActionError('')
    try {
      await copyTextWithFallback(linkOnly ? prepared.model.url : prepared.model.text)
      onShareAction?.()
      setNotice(
        linkOnly
          ? 'Link copiado. Use no adesivo de link dos Stories.'
          : 'Mensagem e link copiados.'
      )
    } catch {
      setActionError('Selecione e copie o texto da mensagem abaixo.')
    }
  }

  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="participant-share-title"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <div>
            <span>ESPALHE ESSA HISTÓRIA</span>
            <h2 id="participant-share-title">Sua jornada merece ir longe.</h2>
          </div>
          <button
            type="button"
            autoFocus
            className={styles.close}
            onClick={onClose}
            aria-label="Fechar compartilhamento"
          >
            <Icon icon="mdi:close" />
          </button>
        </header>
        <div className={styles.body}>
          <div className={styles.preview} aria-busy={!prepared && !error}>
            {prepared ? (
              <img
                src={prepared.previewUrl}
                alt="Prévia do seu card com avatar, campanha e conquistas"
              />
            ) : (
              <div className={styles.loading}>
                {error ? (
                  <>
                    <Icon icon="mdi:image-off-outline" />
                    <p role="alert">{error}</p>
                    <button type="button" onClick={onRetry}>
                      Tentar novamente
                    </button>
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:creation" className={styles.pulse} />
                    <p role="status">Preparando sua história…</p>
                    <span>Reunindo seu avatar, conquistas e campanha.</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={styles.options}>
            <p className={styles.lead}>Um convite com a sua cara.</p>
            <p className={styles.description}>
              Compartilhe sua participação e inspire alguém a fazer parte. Seu card está
              no formato de Stories.
            </p>
            {prepared && (
              <>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={isSharing}
                  onClick={supportsFiles ? () => void share() : download}
                >
                  <Icon
                    icon={supportsFiles ? 'mdi:share-variant-outline' : 'mdi:download'}
                  />
                  {isSharing
                    ? 'Abrindo compartilhamento…'
                    : supportsFiles
                      ? 'Compartilhar imagem'
                      : 'Baixar imagem para Stories'}
                </button>
                <button
                  type="button"
                  className={styles.whatsapp}
                  disabled={isSharing}
                  onClick={
                    supportsFiles ? () => void share() : () => setWhatsappFallback(true)
                  }
                >
                  <Icon icon="mdi:whatsapp" />
                  Compartilhar no WhatsApp
                </button>
                <p className={styles.hint}>
                  {supportsFiles
                    ? 'Escolha o WhatsApp ou outro app na lista do aparelho. A imagem vai acompanhada da mensagem nos apps compatíveis.'
                    : 'Salve o card para publicar no Instagram ou anexar à conversa no WhatsApp.'}
                </p>
                {whatsappFallback && (
                  <div className={styles.fallback}>
                    <p>
                      1. Baixe a imagem.
                      <br />
                      2. Abra o WhatsApp e anexe o card à mensagem.
                    </p>
                    <button type="button" onClick={download}>
                      Baixar imagem
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(prepared.model.text)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onShareAction}
                    >
                      Abrir WhatsApp com a mensagem ↗
                    </a>
                  </div>
                )}
                <div className={styles.tools}>
                  {supportsFiles && (
                    <button type="button" onClick={download}>
                      <Icon icon="mdi:download" />
                      Baixar Story
                    </button>
                  )}
                  <button type="button" onClick={() => void copy(true)}>
                    <Icon icon="mdi:link-variant" />
                    Copiar link
                  </button>
                </div>
                <label
                  className={styles.messageLabel}
                  htmlFor="participant-share-message"
                >
                  MENSAGEM PARA ACOMPANHAR
                </label>
                <textarea
                  id="participant-share-message"
                  value={prepared.model.text}
                  readOnly
                  rows={5}
                  className={styles.message}
                />
                <button type="button" className={styles.copy} onClick={() => void copy()}>
                  <Icon icon="mdi:content-copy" />
                  Copiar mensagem e link
                </button>
              </>
            )}
            {notice && (
              <p className={styles.notice} role="status">
                {notice}
              </p>
            )}
            {actionError && (
              <p className={styles.error} role="alert">
                {actionError}
              </p>
            )}
          </div>
        </div>
      </div>
    </dialog>,
    document.body
  )
}
